import { IconButton, Paper, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React from 'react';
import Colors from '../../utils/color';

/**
 * ConsoleCard — the shared HITL 3.0 console card frame.
 *
 * One reusable shell for every console card (Customer Pulse / Live Transcript /
 * Action Center on the left, Agent Cart / POS Cart on the right): a white card
 * with an inline header (outlined icon + uppercase title + optional right slot +
 * accordion chevron), an inset divider, and a collapsible body. Previously each
 * card re-implemented this skeleton (identical `card`/`header`/`title`/`chevIcon`
 * style blocks); this collapses them onto one contract so the fonts, spacing and
 * collapse behavior stay consistent by construction.
 *
 * Collapse is controlled by the parent (open + onToggle) so the resizable panel
 * and the chevron stay in sync (see useLeftStackCollapse / useRightStackCollapse).
 *
 * Slots:
 *   headerRight  — node between the title and the chevron (e.g. the cart's item count)
 *   headerAction — REPLACES the chevron entirely (e.g. the transcript takeover Close button)
 *   footer       — always-visible content below the body (e.g. Pulse's AI status band);
 *                  renders in both open and collapsed states
 *   children     — the collapsible body; shown only when `open`
 */

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.alto}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  // position:relative so a headerAction can absolutely-position itself against
  // the card corner (the transcript takeover Close button).
  header: {
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  headerIcon: {
    color: Colors.readBack,
    fontSize: '1rem',
    flexShrink: 0,
  },
  title: {
    flex: 1,
    minWidth: 0,
    color: Colors.readBack,
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  chevIcon: {
    fontSize: '1.35rem',
    transition: 'transform .15s ease',
  },
  // Expanded: point the arrow up (click-to-collapse), matching the mockup's "^".
  chevIconOpen: {
    transform: 'rotate(180deg)',
  },
  // Inset rule below the header — 1px Colors.alto, inset by the card padding,
  // with ~1.25 breathing room under the heading. The body supplies its own
  // matching paddingTop below the line.
  divider: {
    flexShrink: 0,
    marginTop: theme.spacing(1.25),
    borderTop: `1px solid ${Colors.alto}`,
  },
  // keepBodyMounted wrapper: the body stays in the DOM across collapse and is
  // hidden via CSS (not unmounted). Used for heavy bodies (Agent Cart's
  // OrderReview) whose re-mount is slow and whose effects must keep running
  // while collapsed.
  bodyMounted: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  bodyMountedHidden: {
    display: 'none',
  },
}));

export interface ConsoleCardProps {
  /** Outlined MUI icon element; cloned to receive the shared header-icon style. */
  icon: React.ReactElement;
  /** Header label; rendered uppercase via the shared title style. */
  title: string;
  open: boolean;
  onToggle: () => void;
  /** Node rendered between the title and the chevron (e.g. item count). */
  headerRight?: React.ReactNode;
  /** Replaces the chevron entirely (e.g. the transcript takeover Close button). */
  headerAction?: React.ReactNode;
  /** Show the inset divider below the header when open (default true). */
  showDivider?: boolean;
  /** Always-visible content below the body (e.g. Pulse's AI status band). */
  footer?: React.ReactNode;
  /**
   * Keep the body mounted when collapsed (hidden via CSS) instead of
   * unmounting it. Use for heavy bodies whose re-mount is slow / whose effects
   * must keep running while collapsed (e.g. Agent Cart's OrderReview). Default
   * false — light bodies just unmount.
   */
  keepBodyMounted?: boolean;
  /** Collapsible body; shown only when open (or hidden if keepBodyMounted). */
  children?: React.ReactNode;
  testId?: string;
  /** Extra class(es) merged onto the card root. */
  className?: string;
  /** Card vertical / horizontal padding in theme.spacing multiples. */
  py?: number;
  px?: number;
}

const ConsoleCard = ({
  icon,
  title,
  open,
  onToggle,
  headerRight,
  headerAction,
  showDivider = true,
  footer,
  keepBodyMounted = false,
  children,
  testId,
  className,
  py = 1,
  px = 1.25,
}: ConsoleCardProps) => {
  const classes = useStyles();

  const styledIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
        className: [
          classes.headerIcon,
          (icon.props as { className?: string }).className,
        ]
          .filter(Boolean)
          .join(' '),
      })
    : icon;

  return (
    <Paper
      className={`${classes.card}${className ? ` ${className}` : ''}`}
      elevation={0}
      data-testid={testId}
      sx={{ py, px }}
    >
      <div className={classes.header}>
        {styledIcon}
        <span className={classes.title}>{title}</span>
        {headerRight}
        {headerAction ?? (
          <IconButton
            size="small"
            aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
            onClick={onToggle}
            // sx (not makeStyles) so ml:auto/padding beat MUI ButtonBase's own
            // emotion styles — this right-anchors the chevron to the card corner.
            sx={{ ml: 'auto', mr: -0.5, p: 0.25, color: '#79808a' }}
          >
            <KeyboardArrowDownIcon
              className={`${classes.chevIcon} ${
                open ? classes.chevIconOpen : ''
              }`}
            />
          </IconButton>
        )}
      </div>

      {open && showDivider && <div className={classes.divider} />}
      {keepBodyMounted
        ? children != null && (
            <div
              className={open ? classes.bodyMounted : classes.bodyMountedHidden}
            >
              {children}
            </div>
          )
        : open && children}
      {footer}
    </Paper>
  );
};

export default ConsoleCard;
